import React, { useState, useMemo } from 'react';
import { logger } from '@/utils/logger';
import { GoogleMap, useLoadScript, Marker, DirectionsRenderer, Libraries } from '@react-google-maps/api';
import { toast } from 'sonner';
import { OrderForDelivery } from '@/pages/admin/Orders';

// Extend the OrderForDelivery interface to include status
interface ExtendedOrderForDelivery extends OrderForDelivery {
  status?: string;
}

// Define the types for our component
interface RouteCalculatorProps {
  orders: ExtendedOrderForDelivery[];
  onRouteCalculated?: (route: any) => void;
}

interface RouteData {
  route: RouteStop[];
  totalDistance: number;
  totalDuration: number;
  directions: any;
}

interface RouteStop {
  orderId: string;
  customerName: string;
  address: string;
  phone: string | null;
  email: string;
  eta: string;
  etaFormatted: string;
  delivery_date: string;
}

interface DriverRoute {
  driver1: RouteData | null;
  driver2: RouteData | null;
}

// Define the libraries array outside the component
const libraries: Libraries = ['places'];

const RouteCalculator: React.FC<RouteCalculatorProps> = ({ orders, onRouteCalculated }) => {
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [startAddress, setStartAddress] = useState<string>('');
  const [endAddress, setEndAddress] = useState<string>('');
  const [useStartAsEnd, setUseStartAsEnd] = useState<boolean>(true);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [routeData, setRouteData] = useState<RouteData | null>(null);
  const [showMap, setShowMap] = useState<boolean>(false);
  const [isSendingNotifications, setIsSendingNotifications] = useState<boolean>(false);
  const [directionsResponse, setDirectionsResponse] = useState<google.maps.DirectionsResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrdersData, setSelectedOrdersData] = useState<ExtendedOrderForDelivery[]>([]);
  const [useTwoDrivers, setUseTwoDrivers] = useState<boolean>(false);
  const [routeDataTwoDrivers, setRouteDataTwoDrivers] = useState<DriverRoute>({ driver1: null, driver2: null });
  const [stopTimeMinutes, setStopTimeMinutes] = useState<number>(10);
  const [startTime, setStartTime] = useState<string>('17:30');

  // Load Google Maps script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries,
  });

  // Handle order selection
  const handleOrderSelection = (orderId: string) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId) 
        : [...prev, orderId]
    );
  };

  // Select all orders
  const selectAllOrders = () => {
    const allOrderIds = orders
      .filter(order => order.profiles?.address)
      .map(order => order.id);
    setSelectedOrders(allOrderIds);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedOrders([]);
  };

  // Calculate route for a single driver
  const calculateSingleRoute = async (
    driverOrders: ExtendedOrderForDelivery[],
    driverStartAddress: string,
    driverEndAddress: string,
    useStartAsEndAddress: boolean
  ) => {
    if (driverOrders.length === 0) {
      return null;
    }

    // Create waypoints
    const waypoints = driverOrders.map(order => ({
      location: order.profiles?.address || '',
      stopover: true
    }));

    // Use DirectionsService
    const directionsService = new google.maps.DirectionsService();
    
    return new Promise<RouteData>((resolve, reject) => {
      directionsService.route(
        {
          origin: driverStartAddress,
          destination: useStartAsEndAddress ? driverStartAddress : (driverEndAddress || driverStartAddress),
          waypoints: waypoints,
          optimizeWaypoints: true,
          travelMode: google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === google.maps.DirectionsStatus.OK && result) {
            const optimizedOrder = result.routes[0].waypoint_order;
            const optimizedDestinations = optimizedOrder.map(index => driverOrders[index]);
            
            // Calculate ETAs
            let currentEta = new Date();
            // Set the start time based on user input
            const [hours, minutes] = startTime.split(':').map(Number);
            currentEta.setHours(hours, minutes, 0, 0);

            const routeWithEtas = optimizedDestinations.map((order, index) => {
              const legDurationSeconds = result.routes[0].legs[index].duration?.value || 0;
              currentEta = new Date(currentEta.getTime() + legDurationSeconds * 1000);
              const stopEta = new Date(currentEta);
              currentEta = new Date(currentEta.getTime() + stopTimeMinutes * 60 * 1000);
              
              return {
                orderId: order.id,
                customerName: `${order.profiles?.first_name || ''} ${order.profiles?.last_name || ''}`.trim(),
                address: order.profiles?.address || '',
                phone: order.profiles?.phone || '',
                email: order.profiles?.email || '',
                eta: stopEta.toISOString(),
                etaFormatted: stopEta.toLocaleTimeString('sv-SE', { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: false,
                  timeZone: 'Europe/Stockholm'
                }),
                delivery_date: order.delivery_date
              };
            });

            const totalDistance = result.routes[0].legs.reduce(
              (sum, leg) => sum + (leg.distance?.value || 0), 0
            );
            const totalDuration = result.routes[0].legs.reduce(
              (sum, leg) => sum + (leg.duration?.value || 0), 0
            ) + (stopTimeMinutes * 60 * (driverOrders.length - 1)); // Add stop time for all stops except the last one

            resolve({
              route: routeWithEtas,
              totalDistance,
              totalDuration,
              directions: result
            });
          } else {
            reject(new Error(`Failed to calculate route: ${status}`));
          }
        }
      );
    });
  };

  // Calculate routes for both drivers
  const calculateRouteInBrowser = async () => {
    if (!isLoaded) {
      toast.error('Google Maps is not loaded yet');
      return;
    }

    if (!startAddress) {
      toast.error('Please enter a start address');
      return;
    }

    if (selectedOrders.length === 0) {
      toast.error('Please select at least one order');
      return;
    }

    setIsCalculating(true);

    try {
      const selectedOrdersData = orders.filter(order => 
        selectedOrders.includes(order.id) && order.profiles?.address
      );

      if (selectedOrdersData.length === 0) {
        toast.error('No valid delivery addresses found');
        setIsCalculating(false);
        return;
      }

      if (useTwoDrivers) {
        // Split orders into two groups based on proximity
        const midPoint = Math.floor(selectedOrdersData.length / 2);
        const driver1Orders = selectedOrdersData.slice(0, midPoint);
        const driver2Orders = selectedOrdersData.slice(midPoint);

        const [driver1Route, driver2Route] = await Promise.all([
          calculateSingleRoute(driver1Orders, startAddress, endAddress, useStartAsEnd),
          calculateSingleRoute(driver2Orders, startAddress, endAddress, useStartAsEnd)
        ]);

        setRouteDataTwoDrivers({
          driver1: driver1Route,
          driver2: driver2Route
        });

        // Return only driver1's route data for the main display
        if (driver1Route) {
          return {
            route: [...(driver1Route.route || []), ...(driver2Route?.route || [])],
            totalDistance: (driver1Route.totalDistance || 0) + (driver2Route?.totalDistance || 0),
            totalDuration: (driver1Route.totalDuration || 0) + (driver2Route?.totalDuration || 0),
            directions: driver1Route.directions
          };
        }
        return null;
      } else {
        const singleRoute = await calculateSingleRoute(selectedOrdersData, startAddress, endAddress, useStartAsEnd);
        return singleRoute;
      }
    } catch (error) {
      logger.error('Error calculating route in browser:', error);
      throw error;
    }
  };

  // Update the calculateRoute function
  const calculateRoute = async () => {
    if (!startAddress) {
      setError('Please enter a start address');
      return;
    }

    if (selectedOrders.length === 0) {
      setError('Please select at least one order');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get selected orders data
      const selectedOrdersData = orders.filter(order => 
        selectedOrders.includes(order.id) && order.profiles?.address
      );

      if (selectedOrdersData.length === 0) {
        throw new Error('No valid delivery addresses found');
      }

      // Calculate route directly in the browser
      const routeData = await calculateRouteInBrowser();
      if (routeData && routeData.directions) {
        setRouteData(routeData);
        setShowMap(true);
        setDirectionsResponse(routeData.directions);
        if (onRouteCalculated) {
          onRouteCalculated(routeData);
        }
      } else {
        throw new Error('Failed to calculate route');
      }
    } catch (err) {
      logger.error('Error calculating route:', err);
      setError(err instanceof Error ? err.message : 'Failed to calculate route');
      toast.error(err instanceof Error ? err.message : 'Failed to calculate route');
    } finally {
      setIsLoading(false);
      setIsCalculating(false);
    }
  };

  // Send SMS notifications
  const sendNotifications = async () => {
    if (!routeData || !routeData.route || routeData.route.length === 0) {
      toast.error('No route to send notifications for');
      return;
    }

    setIsSendingNotifications(true);

    try {
      const response = await fetch('/api/send-delivery-notifications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          deliveries: routeData.route
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send notifications');
      }

      const data = await response.json();
      toast.success(`${data.notifications} notifications sent`);
    } catch (error) {
      logger.error('Error sending notifications:', error);
      toast.error(error instanceof Error ? error.message : 'An error occurred while sending notifications');
    } finally {
      setIsSendingNotifications(false);
    }
  };

  // Generate driver link
  const generateDriverLink = (driverNumber?: number) => {
    logger.debug('Generating driver link...', { driverNumber, useTwoDrivers });
    let routeToShare;
    
    if (useTwoDrivers && driverNumber) {
      const driverRoute = driverNumber === 1 ? routeDataTwoDrivers.driver1 : routeDataTwoDrivers.driver2;
      logger.debug('Two drivers mode, selected driver route:', driverRoute);
      if (!driverRoute) {
        toast.error('No route data available for this driver');
        return;
      }
      routeToShare = driverRoute;
    } else {
      logger.debug('Single driver mode, route data:', routeData);
      if (!routeData) {
        toast.error('No route data available');
        return;
      }
      routeToShare = routeData;
    }

    try {
      // Clean up old routes (older than 24 hours)
      const cleanup = () => {
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('route-')) {
            try {
              const timestamp = parseInt(key.split('-')[2]);
              if (Date.now() - timestamp > 24 * 60 * 60 * 1000) {
                localStorage.removeItem(key);
              }
            } catch (e) {
              logger.warn('Error parsing route timestamp:', e);
            }
          }
        });
      };

      // Try cleanup first
      cleanup();

      // Create a unique ID for this route
      const routeId = `route-${driverNumber || 1}-${Date.now()}`;
      logger.debug('Generated route ID:', routeId);
      
      // Simplify the route data for storage
      const routeDataForStorage = {
        route: routeToShare.route,
        totalDistance: routeToShare.totalDistance,
        totalDuration: routeToShare.totalDuration,
        // Only store essential direction data
        directions: routeToShare.directions ? {
          routes: routeToShare.directions.routes.map((route: any) => ({
            legs: route.legs.map((leg: any) => ({
              distance: leg.distance,
              duration: leg.duration,
              start_location: {
                lat: typeof leg.start_location.lat === 'function' ? leg.start_location.lat() : leg.start_location.lat,
                lng: typeof leg.start_location.lng === 'function' ? leg.start_location.lng() : leg.start_location.lng
              },
              end_location: {
                lat: typeof leg.end_location.lat === 'function' ? leg.end_location.lat() : leg.end_location.lat,
                lng: typeof leg.end_location.lng === 'function' ? leg.end_location.lng() : leg.end_location.lng
              },
              steps: leg.steps.map((step: any) => ({
                instructions: step.instructions,
                distance: step.distance,
                duration: step.duration,
                start_location: {
                  lat: typeof step.start_location.lat === 'function' ? step.start_location.lat() : step.start_location.lat,
                  lng: typeof step.start_location.lng === 'function' ? step.start_location.lng() : step.start_location.lng
                },
                end_location: {
                  lat: typeof step.end_location.lat === 'function' ? step.end_location.lat() : step.end_location.lat,
                  lng: typeof step.end_location.lng === 'function' ? step.end_location.lng() : step.end_location.lng
                }
              }))
            }))
          }))
        } : null
      };
      
      try {
        // Try to store the route data
        localStorage.setItem(routeId, JSON.stringify(routeDataForStorage));
      } catch (storageError) {
        logger.warn('Initial storage attempt failed:', storageError);
        
        // If storage fails, try more aggressive cleanup
        cleanup();
        // Remove all existing routes if still needed
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('route-')) {
            localStorage.removeItem(key);
          }
        });
        
        // Try storing again
        try {
          localStorage.setItem(routeId, JSON.stringify(routeDataForStorage));
        } catch (finalError) {
          throw new Error('Could not store route data even after cleanup');
        }
      }
      
      // Generate a link to the driver interface
      const baseUrl = window.location.origin;
      const driverLink = `${baseUrl}/driver/${routeId}`;
      logger.debug('Generated driver link:', driverLink);
      
      // Copy to clipboard
      navigator.clipboard.writeText(driverLink)
        .then(() => {
          logger.debug('Link copied to clipboard');
          toast.success(`Driver ${driverNumber ? driverNumber + ' ' : ''}link copied to clipboard`);
        })
        .catch(err => {
          logger.error('Could not copy link: ', err);
          // Fallback for clipboard API failure
          const textarea = document.createElement('textarea');
          textarea.value = driverLink;
          document.body.appendChild(textarea);
          textarea.select();
          try {
            document.execCommand('copy');
            toast.success(`Driver ${driverNumber ? driverNumber + ' ' : ''}link copied to clipboard`);
          } catch (e) {
            logger.error('Fallback copy failed:', e);
            toast.error('Could not copy link. Please copy this URL manually: ' + driverLink);
          }
          document.body.removeChild(textarea);
        });
    } catch (error) {
      logger.error('Error generating driver link:', error);
      toast.error('Failed to generate driver link: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  // Render loading state
  if (!isLoaded) return <div>Loading maps...</div>;
  if (loadError) return <div>Error loading Google Maps</div>;

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-lg font-medium mb-4">Route Planner</h2>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="startAddress" className="block text-sm font-medium text-gray-700 mb-1">
            Start Address
          </label>
          <input
            type="text"
            id="startAddress"
            value={startAddress}
            onChange={(e) => setStartAddress(e.target.value)}
            placeholder="Enter start address"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 mb-1">
              Start Time
            </label>
            <input
              type="time"
              id="startTime"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="useStartAsEnd"
              checked={useStartAsEnd}
              onChange={(e) => setUseStartAsEnd(e.target.checked)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="useStartAsEnd" className="ml-2 block text-sm text-gray-900">
              Return to start address
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="useTwoDrivers"
              checked={useTwoDrivers}
              onChange={(e) => setUseTwoDrivers(e.target.checked)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="useTwoDrivers" className="ml-2 block text-sm text-gray-900">
              Split route between two drivers
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <label htmlFor="stopTime" className="block text-sm text-gray-900">
              Stop time (minutes):
            </label>
            <input
              type="number"
              id="stopTime"
              value={stopTimeMinutes}
              onChange={(e) => setStopTimeMinutes(Math.max(1, parseInt(e.target.value) || 10))}
              min="1"
              className="w-20 px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        {!useStartAsEnd && (
          <div>
            <label htmlFor="endAddress" className="block text-sm font-medium text-gray-700 mb-1">
              End Address
            </label>
            <input
              type="text"
              id="endAddress"
              value={endAddress}
              onChange={(e) => setEndAddress(e.target.value)}
              placeholder="Enter end address (if different from start)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        )}
      </div>

      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-sm font-medium text-gray-700">Select orders for delivery</h3>
          <div className="space-x-2">
            <button
              type="button"
              onClick={selectAllOrders}
              className="text-xs text-indigo-600 hover:text-indigo-900"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs text-red-600 hover:text-red-900"
            >
              Clear selection
            </button>
          </div>
        </div>
        
        <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md">
          {/* Group orders by delivery date */}
          {(() => {
            // Create an object with delivery dates as keys and arrays of orders as values
            const ordersByDate = orders
              .filter(order => order.profiles?.address)
              .reduce((acc, order) => {
                const date = order.delivery_date;
                if (!acc[date]) {
                  acc[date] = [];
                }
                acc[date].push(order);
                return acc;
              }, {} as Record<string, ExtendedOrderForDelivery[]>);
            
            // Sort the dates in ascending order
            const sortedDates = Object.keys(ordersByDate).sort();
            
            // Return the sorted and grouped orders
            return sortedDates.map(date => (
              <div key={date}>
                <div className="sticky top-0 bg-gray-100 p-2 font-semibold text-gray-700 border-b border-gray-200">
                  {date}
                </div>
                {ordersByDate[date].map(order => (
                  <div 
                    key={order.id}
                    className={`flex items-center p-2 hover:bg-gray-50 ${
                      selectedOrders.includes(order.id) ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      id={`order-${order.id}`}
                      checked={selectedOrders.includes(order.id)}
                      onChange={() => handleOrderSelection(order.id)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label 
                      htmlFor={`order-${order.id}`}
                      className="ml-2 block text-sm text-gray-900 cursor-pointer flex-grow"
                    >
                      <span className="font-medium">
                        {order.profiles?.first_name || ''} {order.profiles?.last_name || ''}
                      </span>
                      <span className="text-gray-500 text-xs ml-2">
                        {order.profiles?.address || ''}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            ));
          })()}
        </div>
      </div>

      <div className="flex space-x-2 mb-6">
        <button
          type="button"
          onClick={calculateRoute}
          disabled={isCalculating || selectedOrders.length === 0 || !startAddress}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
            (isCalculating || selectedOrders.length === 0 || !startAddress) ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isCalculating ? 'Calculating...' : 'Calculate Route'}
        </button>
      </div>

      {routeData && useTwoDrivers && (
        <div className="mt-6 space-y-6">
          <div>
            <h3 className="text-lg font-medium mb-2">Driver 1 Route</h3>
            <div className="bg-gray-50 p-4 rounded-md mb-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Total distance</p>
                  <p className="text-lg font-medium">
                    {((routeDataTwoDrivers.driver1?.totalDistance || 0) / 1000).toFixed(1)} km
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Estimated driving time</p>
                  <p className="text-lg font-medium">
                    {Math.floor((routeDataTwoDrivers.driver1?.totalDuration || 0) / 60)} min
                  </p>
                </div>
              </div>
            </div>
            {routeDataTwoDrivers.driver1?.route && (
              <>
                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md mb-4">
                  {routeDataTwoDrivers.driver1.route.map((stop, index) => (
                    <div key={stop.orderId} className="p-3 border-b last:border-b-0">
                      <div className="flex justify-between">
                        <div className="flex items-center">
                          <div className="bg-indigo-100 text-indigo-800 w-6 h-6 rounded-full flex items-center justify-center mr-2">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium">{stop.customerName}</p>
                            <p className="text-sm text-gray-500">{stop.address}</p>
                            <p className="text-xs text-indigo-600">{stop.delivery_date}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">ETA</p>
                          <p className="text-sm">{stop.etaFormatted}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end mb-4">
                  <button
                    type="button"
                    onClick={() => generateDriverLink(1)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Get Driver 1 Link
                  </button>
                </div>
                <div className="h-96 w-full rounded-md overflow-hidden mb-6">
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={{ lat: 59.3293, lng: 18.0686 }}
                    zoom={10}
                  >
                    {routeDataTwoDrivers.driver1.directions && (
                      <DirectionsRenderer
                        directions={routeDataTwoDrivers.driver1.directions}
                        options={{
                          suppressMarkers: false,
                          polylineOptions: {
                            strokeColor: '#4f46e5',
                            strokeWeight: 5,
                          },
                        }}
                      />
                    )}
                  </GoogleMap>
                </div>
              </>
            )}
          </div>

          <div>
            <h3 className="text-lg font-medium mb-2">Driver 2 Route</h3>
            <div className="bg-gray-50 p-4 rounded-md mb-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Total distance</p>
                  <p className="text-lg font-medium">
                    {((routeDataTwoDrivers.driver2?.totalDistance || 0) / 1000).toFixed(1)} km
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Estimated driving time</p>
                  <p className="text-lg font-medium">
                    {Math.floor((routeDataTwoDrivers.driver2?.totalDuration || 0) / 60)} min
                  </p>
                </div>
              </div>
            </div>
            {routeDataTwoDrivers.driver2?.route && (
              <>
                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md mb-4">
                  {routeDataTwoDrivers.driver2.route.map((stop, index) => (
                    <div key={stop.orderId} className="p-3 border-b last:border-b-0">
                      <div className="flex justify-between">
                        <div className="flex items-center">
                          <div className="bg-green-100 text-green-800 w-6 h-6 rounded-full flex items-center justify-center mr-2">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium">{stop.customerName}</p>
                            <p className="text-sm text-gray-500">{stop.address}</p>
                            <p className="text-xs text-indigo-600">{stop.delivery_date}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">ETA</p>
                          <p className="text-sm">{stop.etaFormatted}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end mb-4">
                  <button
                    type="button"
                    onClick={() => generateDriverLink(2)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Get Driver 2 Link
                  </button>
                </div>
                <div className="h-96 w-full rounded-md overflow-hidden mb-6">
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={{ lat: 59.3293, lng: 18.0686 }}
                    zoom={10}
                  >
                    {routeDataTwoDrivers.driver2.directions && (
                      <DirectionsRenderer
                        directions={routeDataTwoDrivers.driver2.directions}
                        options={{
                          suppressMarkers: false,
                          polylineOptions: {
                            strokeColor: '#22c55e',
                            strokeWeight: 5,
                          },
                        }}
                      />
                    )}
                  </GoogleMap>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {routeData && !useTwoDrivers && (
        <div className="mt-6">
          <h3 className="text-lg font-medium mb-2">Route Information</h3>
          
          <div className="bg-gray-50 p-4 rounded-md mb-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Total distance</p>
                <p className="text-lg font-medium">
                  {(routeData.totalDistance / 1000).toFixed(1)} km
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Estimated driving time</p>
                <p className="text-lg font-medium">
                  {Math.floor(routeData.totalDuration / 60)} min
                </p>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-medium mb-2">Deliveries ({routeData.route.length})</h4>
            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md">
              {routeData.route.map((stop, index) => (
                <div key={stop.orderId} className="p-3 border-b last:border-b-0">
                  <div className="flex justify-between">
                    <div className="flex items-center">
                      <div className="bg-indigo-100 text-indigo-800 w-6 h-6 rounded-full flex items-center justify-center mr-2">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium">{stop.customerName}</p>
                        <p className="text-sm text-gray-500">{stop.address}</p>
                        <p className="text-xs text-indigo-600">{stop.delivery_date}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">ETA</p>
                      <p className="text-sm">{stop.etaFormatted}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {showMap && (
            <div className="mb-4">
              <h4 className="text-sm font-medium mb-2">Map</h4>
              <div className="h-96 w-full rounded-md overflow-hidden">
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={{ lat: 59.3293, lng: 18.0686 }}
                  zoom={10}
                >
                  {directionsResponse && (
                    <DirectionsRenderer
                      directions={directionsResponse}
                      options={{
                        suppressMarkers: false,
                        polylineOptions: {
                          strokeColor: '#4f46e5',
                          strokeWeight: 5,
                        },
                      }}
                    />
                  )}
                </GoogleMap>
              </div>
            </div>
          )}

          <div className="flex space-x-2">
            <button
              type="button"
              onClick={sendNotifications}
              disabled={isSendingNotifications}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                isSendingNotifications ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSendingNotifications ? 'Sending...' : 'Send SMS Notifications'}
            </button>
            
            {!useTwoDrivers && (
              <button
                type="button"
                onClick={() => generateDriverLink()}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Get Driver Link
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteCalculator; 